import { Injectable, inject } from '@angular/core';
import { PaymentWaterfallService } from './payment-waterfall.service';
import { FullLoanDeal } from '../models/loan.model';

/**
 * Financial Regression Test Suite
 * 
 * Runs automated tests against hardcoded "truth" values from the ABC Investco spreadsheet
 * to detect calculation drift. Logs results to the console on every app load.
 * 
 * Purpose: Prevent "Financial Logic Drift" where the app approximates spreadsheet logic
 * but uses different underlying physics (dates, rounding, order of operations).
 */

interface TestCase {
    name: string;
    principal: number;
    rate: number;
    startDate: string;
    endDate: string;
    days: number;
    expectedInterest: number;
    description: string;
}

@Injectable({
    providedIn: 'root'
})
export class FinancialTestService {
    private waterfallService = inject(PaymentWaterfallService);

    // Hardcoded "Truth" from ABC Investco Spreadsheet
    private readonly TEST_CASES: TestCase[] = [
        {
            name: 'ABC Investco - Feb 29 to Mar 31, 2024',
            principal: 184000,
            rate: 12.75,
            startDate: '2024-02-29',
            endDate: '2024-03-31',
            days: 32,
            expectedInterest: 2056.77,
            description: 'Leap year period: 1 day Feb + 31 days March'
        }
    ];

    private readonly TOLERANCE = 0.01; // ±1 penny tolerance

    /**
     * Run all regression tests and log results to console
     */
    runTests(): void {
        console.log('%c╔═══════════════════════════════════════════════════════╗', 'color: #0ea5e9; font-weight: bold');
        console.log('%c║  Financial Engine Regression Test Suite             ║', 'color: #0ea5e9; font-weight: bold');
        console.log('%c╚═══════════════════════════════════════════════════════╝', 'color: #0ea5e9; font-weight: bold');
        console.log('');

        let allPassed = true;

        for (const testCase of this.TEST_CASES) {
            const result = this.runTestCase(testCase);
            if (!result.passed) {
                allPassed = false;
            }
        }

        console.log('');
        if (allPassed) {
            console.log('%c═══════════════════════════════════════════════════════', 'color: #22c55e');
            console.log('%c✓ All tests passed', 'color: #22c55e; font-weight: bold; font-size: 14px');
            console.log('%c═══════════════════════════════════════════════════════', 'color: #22c55e');
        } else {
            console.log('%c═══════════════════════════════════════════════════════', 'color: #ef4444');
            console.log('%c✗ Some tests failed - Financial logic may have drifted!', 'color: #ef4444; font-weight: bold; font-size: 14px');
            console.log('%c═══════════════════════════════════════════════════════', 'color: #ef4444');
        }
        console.log('');
    }

    /**
     * Run a single test case
     */
    private runTestCase(testCase: TestCase): { passed: boolean } {
        console.log(`%cTest Case: ${testCase.name}`, 'font-weight: bold; font-size: 12px; color: #3b82f6');
        console.log(`Principal: £${testCase.principal.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} | Rate: ${testCase.rate}% | Convention: Actual/365`);
        console.log(`Period: ${testCase.startDate} to ${testCase.endDate} (${testCase.days} days)`);
        console.log(`Description: ${testCase.description}`);
        console.log('');

        // Construct a minimal mock deal for testing
        const mockDeal = this.createMockDeal(testCase);

        // Run the app's calculation engine
        const snapshot = this.waterfallService.getBorrowerInterestSnapshot(mockDeal, testCase.endDate);
        const calculatedInterest = snapshot.totalInterestAccrued;

        // Compare against expected value
        const difference = calculatedInterest - testCase.expectedInterest;
        const absDifference = Math.abs(difference);
        const passed = absDifference <= this.TOLERANCE;

        // Log result
        if (passed) {
            console.log(
                `%c✅ PASS`,
                'color: #22c55e; font-weight: bold',
                `\nExpected: £${testCase.expectedInterest.toFixed(2)}`,
                `\nCalculated: £${calculatedInterest.toFixed(2)}`,
                `\nDifference: £${difference.toFixed(2)}`
            );
        } else {
            console.error(
                `%c❌ FAIL`,
                'color: #ef4444; font-weight: bold',
                `\nExpected: £${testCase.expectedInterest.toFixed(2)}`,
                `\nCalculated: £${calculatedInterest.toFixed(2)}`,
                `\nDifference: £${difference.toFixed(2)}`,
                `\n⚠️ Variance exceeds ±${this.TOLERANCE} tolerance!`
            );
        }
        console.log('');

        return { passed };
    }

    /**
     * Create a minimal mock FullLoanDeal for testing
     */
    private createMockDeal(testCase: TestCase): FullLoanDeal {
        return {
            loanDeal: {
                id: 'test-deal',
                loanType: 'Standard Bridge',
                loanDealName: 'Financial Regression Test',
                loanDealDescription: 'Automated test deal for financial calculations',
                status: 'FUNDED',
                grossLoanAmount: testCase.principal,
                netLoanAmount: testCase.principal,
                commitmentWindowEndDate: testCase.startDate,
                loanStartDate: testCase.startDate,
                loanEndDate: testCase.endDate,
                durationMonths: 1,
                minimumTermMonths: 1,
                reasonForFinance: 'Test',
                exitStrategy: 'Test',
                legalTeam: 'Test',
                dealReferenceVC: 'TEST001'
            },
            properties: [],
            borrower: {
                id: 'test-borrower',
                loanDealId: 'test-deal',
                borrowerName: 'Test Borrower',
                registrationCode: 'TEST001',
                securityDetails: []
            },
            financialTerms: {
                id: 'test-financial-terms',
                loanDealId: 'test-deal',
                borrowerInterestRate: testCase.rate,
                borrowerDefaultInterestRate: testCase.rate,
                borrowerInterestPayable: 'Monthly in Arrears',
                clientInterestRate: 0,
                clientDefaultInterestRate: 0,
                clientInterestPayable: 'Monthly in Arrears',
                brokerInterestRate: 0,
                arrisInterestRate: 0,
                arrisDefaultInterestRate: 0,
                arrisInterestPayable: 'Monthly in Arrears',
                dayCountConvention: 'Actual/365',
                fees: []
            },
            commitments: [],
            events: [],
            loanLedger: [],
            borrowerLedger: [
                // Single drawdown on loan start date
                {
                    id: 'drawdown-1',
                    loanDealId: 'test-deal',
                    date: testCase.startDate,
                    description: 'Initial Drawdown',
                    amount: testCase.principal,
                    entryType: 'DRAWDOWN'
                }
            ],
            documents: []
        };
    }
}
